import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Text,
  Button,
  Input,
  Textarea,
  Divider,
  Link as FluentLink,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  SpinButton,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Warning20Filled, Dismiss20Regular, Add12Filled, Dismiss12Regular, Info20Regular, ReOrderDotsVertical20Regular } from '@fluentui/react-icons';
import {
  Box24Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  AppsList24Regular,
  Map20Regular,
  Receipt20Regular,
} from '@fluentui/react-icons';
import { isTonnageApplicableProductName, buildLineLabelMap } from '../../lib/tonnage';
import {
  LineTonnageCard,
  useLineTonnageNet,
  type TonnageOverageSummary,
} from '../TonnageSection';
import { ProductSearchInput, type ProductGroup, type StockItem } from '../ProductSearchInput';
import { SectionShell } from './SectionShell';
import MovePhaseMenu, { type MovePhaseOption } from '../MovePhaseMenu';
import BaseDialog from '../BaseDialog';
import { RateTierMap } from '../RateTierMap';
import { apiFetch } from '../../lib/apiFetch';
import { blockNonNumericInput, sanitizeDecimalString } from '../../lib/numericInput';
import { CustomLineDialog, type CustomLineSubmitPayload } from './CustomLineDialog';
import { DiscountDialog } from './DiscountDialog';
import { CouponDialog } from './CouponDialog';
import { WithholdingAdjustmentDialog } from './WithholdingAdjustmentDialog';

const useStyles = makeStyles({
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding('48px', '24px'),
    color: tokens.colorNeutralForeground3,
  },
  tierMapFallback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  // Shortage MessageBar (#2). White fill with a bright-orange (Pumpkin)
  // border + icon; body text stays neutral — the project's accent-not-fill
  // warning language (Fluent's default `warning` intent skews dim/mustard).
  shortageBar: {
    marginBottom: '8px',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    '& .fui-MessageBar__icon': {
      color: tokens.colorPalettePumpkinBorderActive,
    },
  },
  linesTable: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding('8px', '0'),
    // The fixed-width columns (qty stepper + price input + total) can't
    // shrink below ~500px, so on narrow screens scroll the table inside the
    // section card instead of blowing out the whole page width.
    overflowX: 'auto',
  },
  // Wraps a line's grid row plus its (full-width) expandable extra-info field,
  // so the note can span all columns rather than living inside the name cell.
  // The row separator lives here so it sits below the note, not above it.
  lineRowWrap: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    minWidth: '780px',
  },
  lineRow: {
    display: 'grid',
    // Item | Qty | Unit price | Rental cycle | Total | remove
    gridTemplateColumns: '1fr 120px 200px 130px 120px 40px',
    alignItems: 'center',
    columnGap: '12px',
    ...shorthands.padding('10px', '0'),
    fontSize: '14px',
  },
  // Caret + product name on one baseline; caret toggles the extra-info field.
  lineNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  // Inline "Tonnage & receipt" disclosure toggle under a weighed line's name.
  tonnageToggle: {
    marginTop: '2px',
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('2px', '4px'),
    minWidth: 'unset',
  },
  // Height-animated disclosure for the inline tonnage panel. Stays mounted in
  // both states; the grid-rows 0fr→1fr transition expands/collapses smoothly so
  // toggling doesn't snap the rows below. (prefers-reduced-motion disables it.)
  tonnageDisclosure: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transitionProperty: 'grid-template-rows',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  tonnageDisclosureOpen: {
    gridTemplateRows: '1fr',
  },
  tonnageDisclosureInner: {
    ...shorthands.overflow('hidden'),
    minHeight: '0',
  },
  // Twirl-down caret to the left of the line title. Subtle, icon-only.
  lineCaret: {
    minWidth: '20px',
    maxWidth: '20px',
    height: '20px',
    ...shorthands.padding('0'),
    color: tokens.colorNeutralForeground3,
  },
  // Grab handle for dragging a line onto a phase card. Cursor + muted color so
  // it reads as a drag affordance without competing with the line title.
  dragHandle: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'grab',
    color: tokens.colorNeutralForeground3,
    ':active': { cursor: 'grabbing' },
  },
  lineHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 120px 200px 130px 120px 40px',
    columnGap: '12px',
    ...shorthands.padding('8px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    minWidth: '780px',
  },
  lineQty: { textAlign: 'center', display: 'flex', justifyContent: 'center' },
  linePrice: { textAlign: 'left' },
  lineRentalCycle: { textAlign: 'left', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' },
  lineTotal: { textAlign: 'right', fontWeight: '600' },
  // SpinButton caps at 'medium'; bump its height to read as large alongside
  // the size='large' price input so the row's fields match.
  lineQtyInput: {
    minHeight: '40px',
    '& input': { minHeight: '40px' },
  },
  // Stock-unit identifier pills under each line name. Small chips so a
  // line with qty 3 reads as three distinct units, matching Booqable's
  // own pill UI for stock-item selection.
  lineStockPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '6px',
    // Breathing room so the row separator below doesn't kiss the pills.
    marginBottom: '10px',
  },
  lineStockPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding('4px', '6px', '4px', '10px'),
    ...shorthands.borderRadius('999px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    lineHeight: '18px',
  },
  lineStockPillRemove: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    ...shorthands.padding('0'),
    ...shorthands.borderRadius('999px'),
    ...shorthands.border('none'),
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
  },
  // Empty-slot pill: "+ Specify stock item". Brand-tinted outline so it
  // reads as an action rather than a static label.
  lineStockPillEmpty: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontFamily: 'inherit',
    color: tokens.colorBrandForeground1,
    backgroundColor: 'transparent',
    ...shorthands.padding('4px', '10px'),
    ...shorthands.borderRadius('999px'),
    ...shorthands.border('1px', 'dashed', tokens.colorBrandStroke1),
    lineHeight: '18px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
  },
  // "Extra information" note, revealed by the caret. Standard bordered,
  // vertically-resizable textarea spanning the full row width. Force the inner
  // <textarea> to fill the bordered root — otherwise it falls back to its
  // intrinsic (cols-based) width and reads as a box-within-a-box.
  lineExtraInfo: {
    display: 'block',
    width: '100%',
    marginTop: '4px',
    marginBottom: '10px',
    // Start at a single line; the user grows it via the bottom-right grip.
    '& textarea': {
      width: '100%',
      minHeight: 'auto',
    },
  },
  // Saved note rendered as static text when the order is locked / completed.
  lineExtraInfoReadOnly: {
    display: 'block',
    width: '100%',
    marginTop: '4px',
    marginBottom: '10px',
    whiteSpace: 'pre-wrap',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  // Full-width rule separating the line title from its stock-item pills.
  lineTitleDivider: {
    marginTop: '6px',
    marginBottom: '6px',
  },
  // Picker popover layout — scrollable grid of available stock items.
  stockPickerPopover: {
    ...shorthands.padding('12px'),
    maxWidth: '420px',
    maxHeight: '360px',
    overflowY: 'auto',
  },
  stockPickerHeader: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
    marginBottom: '8px',
  },
  stockPickerGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  stockPickerPill: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '11px',
    fontFamily: 'monospace',
    ...shorthands.padding('4px', '10px'),
    ...shorthands.borderRadius('999px'),
    ...shorthands.border('1px', 'solid', tokens.colorPaletteGreenBorder2),
    color: tokens.colorPaletteGreenForeground2,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    cursor: 'pointer',
    lineHeight: '16px',
    '&:hover': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
    },
  },
  stockPickerPillUnavailable: {
    color: tokens.colorNeutralForegroundDisabled,
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
    ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled),
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
    },
  },
  // Red variant — booked for another order during this order's window.
  stockPickerPillBooked: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    ...shorthands.borderColor(tokens.colorPaletteRedBorder2),
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
  },
  stockPickerEmpty: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  // Inline Unit Price editor. Compact width + right-aligned digits keep the
  // column visually identical to its read-only Text predecessor; staff click
  // to edit, blur or Enter to commit. Empty-price rows get an orange (Pumpkin)
  // border so the eye lands on lines that still need a manual quote.
  priceInput: {
    width: '130px',
    '& input': {
      textAlign: 'left',
    },
  },
  // Unpriced Charge cell: white fill with a bright-orange (Pumpkin) border
  // signalling "needs a manual price" — accent-not-fill warning language.
  priceInputEmpty: {
    width: '130px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
  },
  // Attention card auto-shown above an unpriced Charge cell when the order has
  // no service location (no tier → no auto price). White fill with a bright-
  // orange (Pumpkin) border + icon; neutral body text reads at a glance.
  locationHintCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    maxWidth: '230px',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    color: tokens.colorNeutralForeground1,
    fontSize: '13px',
    fontWeight: 600,
  },
  // Applied via the Input's `input` slot prop so the className lands
  // directly on the inner <input> element. Descendant selectors (& input)
  // lose specificity against Fluent's internal styles; slot-prop wins.
  // Neutral body text on the white fill; the orange root border (priceInputEmpty)
  // carries the "needs a manual price" warning.
  priceInputInner: {
    textAlign: 'left',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    '&::placeholder': {
      color: tokens.colorNeutralForeground1,
      opacity: 0.6,
    },
  },
  lineTotalPending: {
    textAlign: 'right',
    color: tokens.colorNeutralForeground3,
  },
  summarySection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    ...shorthands.padding('24px', '0'),
    // The 400px totals block can't sit beside "Add line" on a phone, so stack
    // them — "Add line" on top, totals full-width below — within the viewport.
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      rowGap: '16px',
    },
  },
  summaryRight: {
    minWidth: '400px',
    '@media (max-width: 768px)': {
      minWidth: 0,
      width: '100%',
    },
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('8px', '0'),
  },
  summaryLabel: {
    fontSize: '14px',
  },
  summaryValue: {
    fontSize: '14px',
    fontWeight: '600',
  },
  summaryLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    fontSize: '14px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  summaryDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke1,
    ...shorthands.margin('12px', '0'),
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('12px', '0'),
    fontSize: '16px',
    fontWeight: '600',
  },
});

// Minimal shape for an order line as rendered in the products list. Parent
// (OrderDetails) extracts these from the Booqable order.included array.
export interface OrderLineStockUnit {
  id: string;          // stock_item_id (for remove API)
  identifier: string;  // display label (e.g. "FRONT_LOADER-11")
}

export interface OrderLineRow {
  id: string;
  itemId?: string;
  name: string;
  quantity: number;
  priceInCents: number;
  totalInCents: number;
  // Total stock for the product, fetched separately via inventory-levels.
  // Used as the fallback qty-SpinButton cap — null/undefined = uncapped.
  stockCount?: number;
  // Window-available count (units free for THIS order's rental window), from
  // the catalog's per-variation availability. Preferred over stockCount for the
  // qty cap so staff can't push a line past what's actually bookable for the
  // window. Undefined when no window is set — falls back to stockCount.
  availableCount?: number;
  // Specific stock units assigned to this line's planning. Empty until
  // the planning has SIPs; each entry pairs the stock_item_id (for the
  // remove call) with its identifier (for display).
  stockUnits?: OrderLineStockUnit[];
  // Planning UUID, needed for the specify_stock_items call.
  planningId?: string;
  // True when this line's planning has been started (out with the customer) or
  // stopped (returned). Either way Booqable 422s on removing the planning line
  // or clearing/specifying its stock-item pills, so the UI hides those
  // affordances and surfaces a "Started"/"Stopped" status instead.
  started?: boolean;
  stopped?: boolean;
  // Pricing source from the add-line response. Only present on the optimistic
  // row; cleared on order refetch (Booqable lines have no source field).
  pricingSource?: 'tier_table' | 'product_default' | 'product_default_fallback' | 'unresolved';
  // Derived at order fetch: 'product' (has itemId), 'custom' (no itemId),
  // 'convenience_fee' (no itemId + title='Convenience fee'). Used to render
  // the convenience fee as its own summary row and to keep it out of the
  // subtotal displayed to staff.
  kind?: 'product' | 'custom' | 'convenience_fee';
  // Product is trackable (has individual stock units). False for bulk/sales
  // items like the bulldog bag, where "Specify stock item" doesn't apply.
  // Undefined for custom lines / before the catalog resolves — treated as
  // trackable so existing behavior is preserved.
  trackable?: boolean;
  // Booqable product_type ('rental' | 'sales_item' | 'service' | …). Sales
  // items have no rental duration, so the per-line "N days" label is hidden.
  productType?: string;
  // True for the optimistic placeholder shown between picker-select and the
  // add-line POST resolving. Renders a spinner + non-interactive cells; the
  // row is reconciled (real id/price) on success or removed on failure.
  pending?: boolean;
  // Free-text "Extra information" note on the line (Booqable line.
  // extra_information). Surfaced as an inline field under the line name so
  // staff can annotate a line (e.g. gate code, specific unit notes).
  extraInformation?: string;
  // Per-line override of the type-derived display affordances. Inert today —
  // nothing populates it — and intentionally NOT persisted. It's the escape
  // hatch for a future one-off the archetype rulesheet can't express; when a
  // real case appears, populate it in-memory before reaching for a DB column.
  displayOverride?: Partial<LineDisplaySpec>;
}

// Which affordances a given line renders. Resolved per-line by layering a
// base spec ← the archetype rulesheet ← the line's own override (see
// resolveLineDisplaySpec). Centralizes what used to be three scattered
// negative-default conditionals in the row JSX.
export interface LineDisplaySpec {
  stockUnits: boolean;      // "Specify stock item" pills
  rentalDuration: boolean;  // "N days" label beside the unit price
  extraInfo: boolean;       // inline "Extra information" note
  priceEditable: boolean;
  qtyEditable: boolean;
  showQty: boolean;         // render the qty cell at all (false hides it)
  removable: boolean;
}

// Single-axis collapse of (kind × productType × trackable). Each line maps to
// exactly one archetype, keeping the rulesheet a flat, greppable table rather
// than nested predicates.
type LineArchetype =
  | 'rental_trackable'
  | 'rental_bulk'
  | 'sales_item'
  | 'service'
  | 'custom'
  | 'convenience_fee';

// Everything on by default; archetypes subtract what doesn't apply.
const BASE_LINE_SPEC: LineDisplaySpec = {
  stockUnits: true,
  rentalDuration: true,
  extraInfo: true,
  priceEditable: true,
  qtyEditable: true,
  showQty: true,
  removable: true,
};

const LINE_RULESHEET: Record<LineArchetype, Partial<LineDisplaySpec>> = {
  // Roll-off and friends (the "bins"): full treatment EXCEPT quantity. Bins are
  // modeled as one discrete line per physical bin, so a per-line qty multiplier
  // is no longer meaningful — suppress the qty cell and lock it to its default.
  rental_trackable: { showQty: false, qtyEditable: false },
  // Bulk rental (rental product with no individual units) — no unit to specify.
  rental_bulk: { stockUnits: false },
  // Sales item (e.g. bulldog bag): flat sale price, not a per-week rate, and
  // no trackable units.
  sales_item: { stockUnits: false, rentalDuration: false },
  // Service: fixed cost, no rental duration, nothing to track.
  service: { stockUnits: false, rentalDuration: false },
  // One-off custom charge line: free price/qty, but no catalog-derived units.
  custom: { stockUnits: false, rentalDuration: false },
  // System-generated convenience fee: display-only, no annotation or editing.
  convenience_fee: {
    stockUnits: false,
    rentalDuration: false,
    extraInfo: false,
    qtyEditable: false,
    priceEditable: false,
  },
};

const archetypeOf = (line: OrderLineRow): LineArchetype => {
  if (line.kind === 'convenience_fee') return 'convenience_fee';
  if (line.kind === 'custom') return 'custom';
  if (line.productType === 'sales_item') return 'sales_item';
  if (line.productType === 'service') return 'service';
  // Rental: undefined trackable is treated as trackable, preserving the prior
  // `trackable !== false` default for custom lines / pre-catalog-resolve rows.
  return line.trackable !== false ? 'rental_trackable' : 'rental_bulk';
};

// A grapple-truck pickup is a fixed-price pickup service, but Booqable types
// the catalog product as a trackable rental — so it lands on the
// rental_trackable archetype and would offer "Specify stock item" pills. A
// pickup has no individual unit to assign, so suppress that one affordance by
// name. Matched here (not as a per-line displayOverride in the builders) so
// every ProductsSection render site — orders, projects, phases — inherits it
// from one place; layered before displayOverride so an explicit override wins.
const isGrapplePickupLine = (line: OrderLineRow): boolean =>
  /grapple/i.test(line.name ?? '');

// The Heavy material add-on is a fixed, single-charge material fee booked at
// qty=1 — there's no unit to specify and no quantity to vary. Booqable can type
// the catalog product as a trackable rental (same mistyping as grapple), and
// the catalog-derived trackable flag resolves asynchronously, so suppress the
// stock pills AND the qty cell by name here rather than relying on archetype.
// Matched at the resolve site (not in the builders) so every render path —
// orders, projects, phases — inherits it; layered before displayOverride.
const isHeavyMaterialLine = (line: OrderLineRow): boolean =>
  /heavy/i.test(line.name ?? '');

// Front-loader bins are quantity-tracked containers billed flat and dispatched
// by count — they are NOT weighed at the landfill, so no tonnage is captured and
// there's no individual unit to assign. Booqable can type the catalog product as
// a trackable rental (same mistyping as grapple), so suppress the "Specify stock
// item" pills by name here. The tonnage panel is suppressed at its own render
// site (showTonnage) using this same predicate. Matched at the resolve site so
// every render path — orders, projects, phases — inherits it.
const isFrontLoaderLine = (line: OrderLineRow): boolean => {
  const lower = (line.name ?? '').toLowerCase();
  return lower.includes('front') && lower.includes('loader');
};

const resolveLineDisplaySpec = (line: OrderLineRow): LineDisplaySpec => ({
  ...BASE_LINE_SPEC,
  ...LINE_RULESHEET[archetypeOf(line)],
  ...(isGrapplePickupLine(line) ? { stockUnits: false } : {}),
  ...(isHeavyMaterialLine(line) ? { stockUnits: false, showQty: false, qtyEditable: false } : {}),
  ...(isFrontLoaderLine(line)
    // Front loaders are quantity-tracked: hide the per-unit "Specify stock item"
    // pills but keep an editable qty cell (rental_trackable hides qty by default,
    // since trackable lines normally get their count via the pills).
    ? { stockUnits: false, showQty: true, qtyEditable: true }
    : {}),
  ...(line.displayOverride ?? {}),
});

interface ProductsSectionProps {
  callForPrice: boolean;
  tier: string | null;
  municipalityName: string | null;
  /** True when /pricing/resolve returned source='unresolved' — the tier
   *  couldn't be derived. Renders a capture/recapture prompt in place of the
   *  tier badge; line-add still falls through at $0 (enforced server-side). */
  serviceAddressMissing?: boolean;
  /** True once the order has a service address on file. Picks the prompt
   *  wording: false → "capture an address", true → "address captured but
   *  unmapped; recapture to resolve the tier". */
  serviceAddressCaptured?: boolean;
  /** True while the service-address municipality → rate tier is still
   *  resolving (geocode + lookup, or the /pricing/resolve probe). Disables the
   *  product picker so a fast user can't add a line before its tier-derived
   *  price lands — which would otherwise create the line at $0. */
  pricingResolving?: boolean;
  pricingError: string | null;
  isAddingLine: boolean;
  pickupDate: string;
  returnDate: string;
  /** Monthly-rental order: the Rental cycle column renders "Monthly" instead of
   *  a day count for each rental line. Display only — pricing is unchanged. */
  isMonthly?: boolean;
  onProductSelect: (productGroup: ProductGroup, stockItem: StockItem) => void;
  showIcon?: boolean;
  lines?: OrderLineRow[];
  subtotalInCents?: number;
  taxInCents?: number;
  /** Human-readable tax-rate breakdown for the Tax line, e.g. "IVU 11.5%".
   *  Null/undefined when the order isn't taxed. */
  taxLabel?: string | null;
  totalInCents?: number;
  /** PR services-withholding held back by the customer (Sec. 1062.03) — a
   *  positive magnitude in CENTS, rendered as a below-the-line "−$X" deduction
   *  with a net "Balance due" beneath the Total. Omitted / 0 when none applies. */
  prWithholdingInCents?: number;
  /** Rate label for the withholding row, e.g. "10%". */
  prWithholdingRate?: string;
  /** Current manual PR withholding amount override (cents), or null when none is
   *  set (the holdback is rate-computed). Drives the adjustment dialog's seed. */
  withholdingAmountCents?: number | null;
  /** Auto (rate-based) holdback in cents + its rate label — reference shown in
   *  the adjustment dialog so staff can match or override it. */
  autoWithholdingCents?: number;
  autoWithholdingRate?: string;
  /** Persist a manual withholding-amount override (cents; 0 zeroes it, null
   *  clears to auto). When provided (and not readOnly), the summary shows an
   *  "Adjust PR withholding" control. Omitted on /orders/new and read-only. */
  onSetWithholdingAmount?: (cents: number | null) => Promise<void>;
  onUpdateLineQuantity?: (lineId: string, quantity: number) => void;
  /** Commit an inline-edited unit price (cents). Called on blur/Enter
   *  from the Unit Price cell. Used by Tier D, unresolved-tier, and any
   *  general manual-quote scenario where staff override the price. */
  onUpdateLinePrice?: (lineId: string, priceInCents: number) => void;
  /** Commit an inline-edited "Extra information" note on a line. Called on
   *  blur/Enter from the per-line note field. Empty string clears it. */
  onUpdateLineExtraInfo?: (lineId: string, extraInformation: string) => void;
  onRemoveLine?: (lineId: string) => void;
  /** Called after a successful add/remove of a stock-item assignment, with
   *  the delta, so the parent can update the line's pills optimistically
   *  (no slow order refetch needed — assignment doesn't affect totals). */
  onStockItemsChanged?: (change: { lineId: string; add?: OrderLineStockUnit; remove?: string }) => void;
  /** Order id (Booqable or TWM uuid; backend normalizes). Passed to the
   *  stock-item picker so it can ask Booqable for per-unit availability
   *  in the order's rental window. */
  orderIdForStockPicker?: string | null;
  /** TWM local order uuid (not the Booqable id). Drives the inline per-line
   *  tonnage + landfill-receipt panel on weighed (roll-off / front-loader)
   *  lines. Null/omitted on a brand-new order → the panel is hidden. */
  orderLocalId?: string | null;
  /** Fired after an inline tonnage save changes the order's lines (the per-line
   *  overage line was added/updated/removed) so the page can reflect it. */
  onTonnageLinesChanged?: (summary?: TonnageOverageSummary) => void;
  /** Customer name for the order — used to name uploaded tonnage receipts
   *  "[Customer] - [Line] - Tonnage Receipt". Omitted → the raw filename stands. */
  customerName?: string | null;
  /** When set, a recalculation banner is shown above the lines table. */
  recalcBanner?: {
    reason: 'location' | 'period' | 'tax';
    onDismiss: () => void;
    onRecalculate: () => Promise<void>;
  };
  /** When set, renders a dismissable white/orange shortage MessageBar above the
   *  product picker (#2). Populated by the parent after a warn-and-allow add of
   *  a line whose window-available count was 0. `onDismiss` clears it. */
  shortageNotice?: { message: string; onDismiss: () => void } | null;
  /** Add a one-off charge line (title/qty/price/tax_category). */
  onAddCustomLine?: (payload: CustomLineSubmitPayload) => Promise<void>;
  /** Add the heavy add-on product line at qty=1, default price. */
  onAddHeavyAddon?: () => Promise<void>;
  /** Lock the section to view-only: the product picker, qty/price/note editors,
   *  remove buttons, inline tonnage capture, and add-line/discount/coupon
   *  affordances are all hidden. Used on dispatched/completed orders to prevent
   *  accidental edits (the page offers an explicit "Edit order" unlock). */
  readOnly?: boolean;
  /** Section-shell heading. Defaults to "Products"; the phased order view passes
   *  "Unscheduled" so this section reads as the base/unscheduled bucket. */
  title?: string;
  /** Phases a line can be moved into. When set (with onMoveLineToPhase), each
   *  line row shows a Move-to-phase menu in its action cell. */
  phaseOptions?: MovePhaseOption[];
  /** A line's current section id (null = unscheduled), to mark the move menu. */
  lineSectionId?: (lineId: string) => string | null;
  /** Persist a line→phase move (null unschedules). */
  onMoveLineToPhase?: (lineId: string, sectionId: string | null) => void;
  /** Hide the subtotal/tax/total block (keeps the Add-line control). Used by the
   *  phased order's Unscheduled bucket, where these lines are only part of the
   *  order — the order grand total lives in the sidebar instead. */
  hideTotals?: boolean;
  /** Compact mode for phase cards: hides the order-level chrome (tier/pricing
   *  banners, recalc/shortage notices) and the whole summary block (custom-line,
   *  discount, coupon, totals), leaving just the product picker + line rows. */
  compact?: boolean;
  /** Render a drag handle on each line row and make it draggable (dataTransfer
   *  carries the Booqable line id), so lines can be dropped onto a phase. */
  draggableLines?: boolean;
  /** True when this section is a project phase (a phase card). Switches the
   *  empty-state copy from "This order is empty" to "This phase is empty". */
  isPhase?: boolean;
  /** Order-level charges mode (used below the phases of a phased order, where
   *  product lines live in phase cards). Hides the product picker and the
   *  product-y empty state, leaving the order-level lines + the "Add custom
   *  line" control. Pair with hideTotals so the sidebar keeps the grand total. */
  chargesOnly?: boolean;
}

// Inclusive rental days: counts BOTH the delivery day and the pickup day, so
// Jun 25 → Jun 30 reads as 6 days (matches backend billing and the invoice).
const computeDays = (pickup: string, ret: string): number => {
  try {
    const gap = Math.round((new Date(ret).getTime() - new Date(pickup).getTime()) / 86400000);
    return Math.max(1, gap + 1);
  } catch {
    return 1;
  }
};

const fmtUsd = (cents: number) => {
  const dollars = (cents || 0) / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

interface LineQtyProps {
  line: OrderLineRow;
  onUpdate?: (lineId: string, quantity: number) => void;
  /** Render the quantity as static text (order locked / completed). */
  readOnly?: boolean;
}

interface LinePriceInputProps {
  line: OrderLineRow;
  onUpdate?: (lineId: string, priceInCents: number) => void;
  /** Render the price as static text (order locked / completed). */
  readOnly?: boolean;
  /** True when the order has no service location, so no rate tier resolved and
   *  the line couldn't be auto-priced. Surfaces a tooltip on the empty
   *  (orange-bordered) Charge cell pointing the user to add a location. */
  noLocationHint?: boolean;
}

// Per-line inline price editor. Draft state lives locally so typing doesn't
// thrash the parent. Commit on blur or Enter; Esc reverts. Empty price
// (priceInCents = 0) gets an orange (Pumpkin) border to signal "needs your attention" —
// used by Tier D, unresolved-tier, and any line added without a tier-derived
// price.
const LinePriceInput: React.FC<LinePriceInputProps> = ({ line, onUpdate, noLocationHint, readOnly }) => {
  const styles = useStyles();
  const isEmpty = !line.priceInCents;
  // Only nag about the missing location when the cell is actually unpriced —
  // a staff-typed manual price clears the hint even if no tier resolved.
  const showLocationHint = !!noLocationHint && isEmpty;
  const [draft, setDraft] = useState(() =>
    line.priceInCents ? (line.priceInCents / 100).toFixed(2) : ''
  );
  const focusedRef = useRef(false);

  // Sync the draft when the controlled value changes (e.g., refetch lands a
  // new price), but only when the input isn't focused — we don't want a
  // refetch mid-edit to wipe the user's in-progress typing.
  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(line.priceInCents ? (line.priceInCents / 100).toFixed(2) : '');
    }
  }, [line.priceInCents]);

  if (readOnly) return <Text>{fmtUsd(line.priceInCents)}</Text>;

  const commit = () => {
    const raw = parseFloat(draft);
    if (!Number.isFinite(raw) || raw < 0) {
      // Invalid — revert silently to the last good value.
      setDraft(line.priceInCents ? (line.priceInCents / 100).toFixed(2) : '');
      return;
    }
    const cents = Math.round(raw * 100);
    setDraft((cents / 100).toFixed(2));
    if (cents !== line.priceInCents) {
      onUpdate?.(line.id, cents);
    }
  };

  const input = (
    <Input
      size="large"
      type="text"
      inputMode="decimal"
      className={isEmpty ? styles.priceInputEmpty : styles.priceInput}
      input={isEmpty ? { className: styles.priceInputInner } : undefined}
      value={draft}
      placeholder="0.00"
      contentBefore={
        <span
          style={{
            color: isEmpty
              ? tokens.colorNeutralForeground1
              : tokens.colorNeutralForeground3,
          }}
        >
          $
        </span>
      }
      onFocus={(e) => {
        focusedRef.current = true;
        e.target.select();
      }}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      // Controlled value, so sanitizing here also covers paste — invalid
      // characters never persist.
      onChange={(_, data) => setDraft(sanitizeDecimalString(data.value))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(line.priceInCents ? (line.priceInCents / 100).toFixed(2) : '');
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );

  if (!showLocationHint) return input;
  // Auto-open (not hover) so the prompt grabs attention the moment a line lands
  // unpriced. Controlled open is derived purely from showLocationHint, so it
  // can't be dismissed by clicking away — it clears itself once a location
  // resolves or staff type a price. trapFocus stays off so the price input
  // underneath remains editable.
  return (
    <Popover open positioning="above" withArrow trapFocus={false}>
      <PopoverTrigger disableButtonEnhancement>
        <span>{input}</span>
      </PopoverTrigger>
      <PopoverSurface className={styles.locationHintCard}>
        <Warning20Filled
          style={{ color: tokens.colorPalettePumpkinBorderActive, flexShrink: 0 }}
        />
        <span>Select a service location to get proper pricing.</span>
      </PopoverSurface>
    </Popover>
  );
};

// Per-line qty editor: SpinButton + transient over-stock popover. Local state
// lives here so each row tracks its own warning/reset independently.
const LineQty: React.FC<LineQtyProps> = ({ line, onUpdate, readOnly }) => {
  const styles = useStyles();
  // Prefer the window-available count (units bookable for this order's rental
  // window) over physical stock. Falls back to stockCount when no window is
  // set. Shortage is warn-and-allow elsewhere, but the qty cap still pins to
  // what's actually available so staff don't silently over-book a line.
  const capSource =
    line.availableCount != null && line.availableCount > 0
      ? line.availableCount
      : line.stockCount;
  const cap = capSource && capSource > 0 ? capSource : null;
  const [warning, setWarning] = useState<string | null>(null);
  // Bump to force the SpinButton to remount, snapping its display back to the
  // controlled value. SpinButton holds its typed text in internal state until
  // it commits — without a remount, an out-of-range typed value lingers
  // visually even though we ignored it.
  const [resetKey, setResetKey] = useState(0);
  const dismissTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const flagWarning = (message: string) => {
    setWarning(message);
    setResetKey((k) => k + 1);
    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = window.setTimeout(() => setWarning(null), 2500);
  };

  if (readOnly) return <Text>{line.quantity}</Text>;

  return (
    <Popover open={warning !== null} onOpenChange={(_, d) => !d.open && setWarning(null)} positioning="above" withArrow>
      <PopoverTrigger disableButtonEnhancement>
        <span>
          <SpinButton
            key={resetKey}
            size="medium"
            className={styles.lineQtyInput}
            // Quantity is a whole-unit count — block non-digits at the input
            // (no decimal point) so typed garbage never reaches the parse.
            input={{
              inputMode: 'numeric',
              onBeforeInput: (e: React.FormEvent<HTMLInputElement>) =>
                blockNonNumericInput(e, false),
            }}
            min={1}
            max={cap ?? undefined}
            value={line.quantity}
            onChange={(_, data) => {
              const raw =
                data.value ??
                (data.displayValue ? Number(data.displayValue) : NaN);
              if (!Number.isFinite(raw)) return;
              if (raw < 1) {
                flagWarning(`Quantity must be at least 1 — reverted to ${line.quantity}.`);
                return;
              }
              if (cap != null && raw > cap) {
                flagWarning(`Only ${cap} in stock — reverted to ${line.quantity}.`);
                return;
              }
              if (raw !== line.quantity) {
                onUpdate?.(line.id, raw);
              }
            }}
          />
        </span>
      </PopoverTrigger>
      <PopoverSurface
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          fontSize: '13px',
          border: `1px solid ${tokens.colorPalettePumpkinBorderActive}`,
        }}
      >
        <Warning20Filled style={{ color: tokens.colorPalettePumpkinBorderActive }} />
        <span>{warning}</span>
      </PopoverSurface>
    </Popover>
  );
};

// Per-line interactive stock-item assignment: filled pill (X to remove)
// per assigned unit, "+ Specify stock item" pill per shortfall slot.
// Empty-slot pill opens a popover with available units for the product.
interface LineStockPillsProps {
  line: OrderLineRow;
  orderId?: string | null;
  onChange?: (change: { lineId: string; add?: OrderLineStockUnit; remove?: string }) => void;
}
const LineStockPills: React.FC<LineStockPillsProps> = ({ line, orderId, onChange }) => {
  const styles = useStyles();
  const units = line.stockUnits || [];
  // A started/stopped planning is locked — Booqable 422s on clearing or
  // specifying its stock items, so drop both affordances.
  const locked = !!(line.started || line.stopped);
  const shortfall = Math.max(0, (line.quantity || 0) - units.length);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [available, setAvailable] = useState<Array<{ id: string; identifier: string; status: string }>>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const refreshAvailable = async () => {
    if (!line.itemId) return;
    setLoadingAvailable(true);
    setError(null);
    try {
      const qs = orderId ? `?order_id=${encodeURIComponent(orderId)}` : '';
      const res = await apiFetch(`/booqable/products/${encodeURIComponent(line.itemId)}/stock-items${qs}`);
      const body = await res.json().catch(() => ({}));
      const data = body?.data || [];
      // Diagnostic: log the status distribution so we can spot mismatches
      // between Booqable's enum and the picker's color mapping.
      // eslint-disable-next-line no-console
      console.log(
        '[stock picker] fetched',
        data.length,
        'items for product',
        line.itemId,
        orderId ? `(order ${orderId})` : '(no order context)',
        Array.from(new Set(data.map((s: any) => s.status))),
      );
      setAvailable(data);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const removeUnit = async (stockItemId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/booqable/lines/${encodeURIComponent(line.id)}/stock-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remove: [stockItemId] }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        onChange?.({ lineId: line.id, remove: stockItemId });
      } else {
        setError(body?.error || `Remove failed (${res.status})`);
      }
    } finally {
      setBusy(false);
    }
  };

  const addUnit = async (stockItemId: string, identifier: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`/booqable/lines/${encodeURIComponent(line.id)}/stock-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ add: [stockItemId] }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setPickerOpen(false);
        onChange?.({ lineId: line.id, add: { id: stockItemId, identifier } });
      } else {
        // Surface Booqable's reason (e.g. shortage_error) so the user can
        // see WHY a green-looking pill actually wasn't available.
        const details = body?.details;
        const detailMsg =
          (details?.errors?.[0]?.detail) ||
          (details?.errors?.[0]?.title) ||
          (typeof details === 'string' ? details : '');
        setError(
          [body?.error || `Add failed (${res.status})`, detailMsg]
            .filter(Boolean)
            .join(' — ')
        );
      }
    } finally {
      setBusy(false);
    }
  };

  if (units.length === 0 && shortfall === 0) return null;

  const alreadyOnThisLine = new Set(units.map((u) => u.id));

  return (
    <div className={styles.lineStockPills}>
      {units.map((u) => (
        <span key={u.id} className={styles.lineStockPill}>
          {u.identifier}
          {/* A started/stopped unit is locked in Booqable — clearing it would
              422, so drop the remove affordance. */}
          {!locked && (
            <button
              type="button"
              className={styles.lineStockPillRemove}
              aria-label={`Remove ${u.identifier}`}
              title={`Remove ${u.identifier}`}
              disabled={busy}
              onClick={() => removeUnit(u.id)}
            >
              <Dismiss12Regular />
            </button>
          )}
        </span>
      ))}
      {!locked && Array.from({ length: shortfall }).map((_, i) => (
        <Popover
          key={`empty-${i}`}
          open={pickerOpen && i === 0}
          onOpenChange={(_, data) => {
            // Only the first empty pill drives the open state — clicking a
            // picker pill assigns one unit, the popover closes, and the
            // next empty pill (now first) reopens on its own click.
            if (i !== 0) return;
            setPickerOpen(data.open);
            if (data.open) refreshAvailable();
          }}
          positioning="below-start"
        >
          <PopoverTrigger disableButtonEnhancement>
            <button
              type="button"
              className={styles.lineStockPillEmpty}
              disabled={busy || !line.itemId || i !== 0}
              title={i === 0 ? 'Specify stock item' : 'Add this one after the first'}
            >
              <Add12Filled />
              Specify stock item
            </button>
          </PopoverTrigger>
          <PopoverSurface className={styles.stockPickerPopover}>
            <div className={styles.stockPickerHeader}>
              {loadingAvailable ? 'Loading…' : `Pick a stock unit for ${line.name}`}
            </div>
            {error && (
              <div
                style={{
                  fontSize: '12px',
                  color: tokens.colorPaletteRedForeground1,
                  backgroundColor: tokens.colorPaletteRedBackground1,
                  padding: '6px 8px',
                  borderRadius: '4px',
                  marginBottom: '8px',
                }}
              >
                {error}
              </div>
            )}
            {!loadingAvailable && available.length === 0 && (
              <div className={styles.stockPickerEmpty}>No stock items found.</div>
            )}
            <div className={styles.stockPickerGrid}>
              {available.map((s) => {
                const onThisLine = alreadyOnThisLine.has(s.id);
                // Booqable's stock_item_suggestions statuses observed:
                //   available_in_location  → green, click to assign
                //   already_booked         → red, conflicts with this window
                //   overdue                → red, unit's still out past its
                //                            return date on a previous order
                //   at_other_location / archived / out / etc → grey, disabled
                // When called without order_id we fall back to in_stock
                // (plain stock-items list, no availability signal).
                const CONFLICT_STATUSES = new Set([
                  'already_booked',
                  'overdue',
                  'unavailable',
                  'shortage',
                ]);
                const isAvailable =
                  !onThisLine &&
                  (s.status === 'available_in_location' || s.status === 'in_stock');
                const isBooked = !onThisLine && CONFLICT_STATUSES.has(s.status);
                const variantClass = onThisLine
                  ? styles.stockPickerPillUnavailable
                  : isBooked
                    ? styles.stockPickerPillBooked
                    : !isAvailable
                      ? styles.stockPickerPillUnavailable
                      : '';
                // Always include the raw Booqable status in the tooltip so
                // we can see why something rendered the way it did — useful
                // until we've confirmed the full set of statuses Booqable
                // returns in practice.
                const conflictReason: Record<string, string> = {
                  already_booked: 'Booked on another order during this window',
                  // Booqable marks a unit "overdue" when its current
                  // out-with-customer rental is past its scheduled return.
                  // That blocks new bookings until the prior order is
                  // stopped in Booqable — find it via the unit's history.
                  overdue: 'Unit is overdue on a previous rental — stop that order in Booqable first',
                  unavailable: 'Unavailable for this rental window',
                  shortage: 'Booked elsewhere — would cause a shortage',
                };
                const baseTitle = onThisLine
                  ? 'Already on this line'
                  : isBooked
                    ? conflictReason[s.status] || 'Conflicts with this window'
                    : !isAvailable
                      ? `Unavailable: ${s.status}`
                      : `Assign ${s.identifier}`;
                const title = `${baseTitle} [${s.status}]`;
                return (
                  <button
                    key={s.id}
                    type="button"
                    className={`${styles.stockPickerPill} ${variantClass}`}
                    disabled={!isAvailable || busy}
                    title={title}
                    onClick={() => addUnit(s.id, s.identifier)}
                  >
                    {s.identifier}
                  </button>
                );
              })}
            </div>
          </PopoverSurface>
        </Popover>
      ))}
    </div>
  );
};

// Per-line free-text "Extra information" note. Draft lives locally so typing
// doesn't thrash the parent; commit on blur or Enter, Esc reverts. Mirrors the
// LinePriceInput pattern (incl. the focused-ref guard against refetch clobber).
interface LineExtraInfoProps {
  line: OrderLineRow;
  onUpdate?: (lineId: string, extraInformation: string) => void;
  /** Render the saved note as static text (order locked / completed). */
  readOnly?: boolean;
}
const LineExtraInfo: React.FC<LineExtraInfoProps> = ({ line, onUpdate, readOnly }) => {
  const styles = useStyles();
  const [draft, setDraft] = useState(() => line.extraInformation || '');
  const focusedRef = useRef(false);

  // Sync the draft when the controlled value changes (e.g., a refetch lands a
  // new note), but never while focused — that would wipe in-progress typing.
  useEffect(() => {
    if (!focusedRef.current) {
      setDraft(line.extraInformation || '');
    }
  }, [line.extraInformation]);

  if (readOnly) {
    return <Text className={styles.lineExtraInfoReadOnly}>{line.extraInformation}</Text>;
  }

  const commit = () => {
    const next = draft.trim();
    if (next !== (line.extraInformation || '')) {
      onUpdate?.(line.id, next);
    }
  };

  return (
    <Textarea
      size="large"
      resize="vertical"
      rows={1}
      className={styles.lineExtraInfo}
      value={draft}
      placeholder="Extra information"
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onChange={(_, data) => setDraft(data.value)}
      onKeyDown={(e) => {
        // Enter inserts a newline (multiline note); Esc reverts and blurs.
        if (e.key === 'Escape') {
          setDraft(line.extraInformation || '');
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
    />
  );
};

const ProductsSectionBase: React.FC<ProductsSectionProps> = ({
  callForPrice,
  tier,
  municipalityName,
  pricingError,
  isAddingLine,
  pickupDate,
  returnDate,
  isMonthly = false,
  onProductSelect,
  showIcon = true,
  lines = [],
  subtotalInCents,
  taxInCents,
  taxLabel,
  totalInCents,
  prWithholdingInCents,
  prWithholdingRate,
  withholdingAmountCents,
  autoWithholdingCents,
  autoWithholdingRate,
  onSetWithholdingAmount,
  onUpdateLineQuantity,
  onUpdateLinePrice,
  onUpdateLineExtraInfo,
  onRemoveLine,
  onStockItemsChanged,
  orderIdForStockPicker,
  orderLocalId,
  onTonnageLinesChanged,
  customerName,
  serviceAddressMissing = false,
  serviceAddressCaptured = false,
  pricingResolving = false,
  recalcBanner,
  shortageNotice,
  onAddCustomLine,
  onAddHeavyAddon,
  readOnly = false,
  title = 'Products',
  phaseOptions,
  lineSectionId,
  onMoveLineToPhase,
  hideTotals = false,
  compact = false,
  draggableLines = false,
  isPhase = false,
  chargesOnly = false,
}) => {
  const [recalcBusy, setRecalcBusy] = React.useState(false);
  const styles = useStyles();
  const [tierMapOpen, setTierMapOpen] = useState(false);
  // Per-line extra-info disclosure. Undefined = use the default (open iff the
  // line already has a saved note, so existing notes stay visible); an explicit
  // toggle overrides that default for the session.
  const [extraInfoOpen, setExtraInfoOpen] = useState<Record<string, boolean>>({});
  // Per-line tonnage/receipt disclosure. Undefined → default open for weighed
  // lines (so the inline capture UI is visible, not hidden behind a caret); an
  // explicit toggle overrides that for the session.
  const [tonnageOpen, setTonnageOpen] = useState<Record<string, boolean>>({});
  // Per-line net-tons figures for the whole order (one fetch), handed to each
  // inline tonnage panel. Hook is a no-op until orderLocalId resolves.
  const netByLine = useLineTonnageNet(orderLocalId ?? null);
  // Per-line display labels: lines sharing a product name get a positional
  // suffix ("Grapple Truck Pickup 1 / 2") so each line — and the landfill
  // receipt filed under a weighed one — is individually identifiable here, on
  // the line title, and in the Send-Email dialog.
  const lineDisplayLabels = useMemo(() => buildLineLabelMap(lines), [lines]);
  const [customLineOpen, setCustomLineOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [withholdingDialogOpen, setWithholdingDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [addonBusy, setAddonBusy] = useState<null | 'heavy'>(null);
  const days = pickupDate && returnDate ? computeDays(pickupDate, returnDate) : null;
  return (
    <SectionShell title={title} icon={<Box24Regular />} showIcon={showIcon} bare={compact}>
      {/* Tier indicator — surfaces resolved service-location tier + Tier D
          guardrail. Pre-capture state (no service address yet) shows the
          capture prompt instead. See planning/booqable-cutover-playbook.md
          "Pricing Authority" + #150 R7. */}
      {/* The "capture service address to see tier" warning was removed here —
          tier resolution still gates the $0 add server-side; an alternative
          user-facing notification is being explored separately. */}
      {!compact && !serviceAddressMissing && callForPrice && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            borderRadius: '4px',
            // White fill with a bright-orange (Pumpkin) border + icon; neutral
            // body text reads at a glance — accent-not-fill warning language.
            backgroundColor: tokens.colorNeutralBackground1,
            border: `1px solid ${tokens.colorPalettePumpkinBorderActive}`,
            color: tokens.colorNeutralForeground1,
            fontSize: '13px',
            fontWeight: 600,
          }}>
            <Warning20Filled style={{ color: tokens.colorPalettePumpkinBorderActive, flexShrink: 0 }} />
            <span>{municipalityName ?? 'This service location'} requires manual pricing.</span>
          </div>
          <Button
            appearance="subtle"
            size="small"
            icon={<Map20Regular />}
            onClick={() => setTierMapOpen(true)}
            title="View rate tier map"
          >
            View map
          </Button>
        </div>
      )}
      {!compact && !serviceAddressMissing && !callForPrice && tier && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <div style={{
            padding: '6px 10px',
            borderRadius: '4px',
            backgroundColor: '#f3f2f1',
            color: '#605e5c',
            fontSize: '12px',
          }}>
            {municipalityName ?? 'Unknown municipality'} • {tier}
          </div>
          <Button
            appearance="subtle"
            size="small"
            icon={<Map20Regular />}
            onClick={() => setTierMapOpen(true)}
            title="View rate tier map"
          >
            View map
          </Button>
        </div>
      )}
      {!compact && pricingError && (
        <div style={{
          marginBottom: '8px',
          padding: '8px 12px',
          borderRadius: '4px',
          backgroundColor: '#fde7e9',
          color: '#a4262c',
          fontSize: '12px',
        }}>
          {pricingError}
        </div>
      )}
      {recalcBanner && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px',
          padding: '12px 16px',
          borderRadius: '4px',
          border: `1px solid ${tokens.colorNeutralStroke2}`,
          backgroundColor: tokens.colorNeutralBackground1,
        }}>
          <Info20Regular style={{ color: tokens.colorBrandForeground1, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Text weight="semibold" style={{ display: 'block', fontSize: '14px' }}>
              {recalcBanner.reason === 'location' ? 'Service location changed' :
               recalcBanner.reason === 'tax' ? 'Customer tax profile changed' :
               'Rental period changed'}
            </Text>
            <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground2 }}>
              {recalcBanner.reason === 'location'
                ? 'Recalculate prices for the new service location?'
                : recalcBanner.reason === 'tax'
                ? 'Recalculate prices for the new tax profile?'
                : 'Recalculate prices for the new rental period?'}
            </Text>
          </div>
          <Button
            appearance="outline"
            size="medium"
            onClick={recalcBanner.onDismiss}
            disabled={recalcBusy}
          >
            Dismiss
          </Button>
          <Button
            appearance="primary"
            size="medium"
            disabled={recalcBusy}
            onClick={async () => {
              setRecalcBusy(true);
              try {
                await recalcBanner.onRecalculate();
              } finally {
                setRecalcBusy(false);
              }
            }}
          >
            {recalcBusy ? 'Recalculating…' : 'Recalculate prices'}
          </Button>
        </div>
      )}
      {pricingResolving && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          fontSize: '13px',
          color: tokens.colorNeutralForeground3,
        }}>
          <Spinner size="tiny" />
          Resolving service-location pricing…
        </div>
      )}
      {shortageNotice && (
        <MessageBar intent="warning" className={styles.shortageBar}>
          <MessageBarBody>{shortageNotice.message}</MessageBarBody>
          <MessageBarActions>
            <Button
              appearance="transparent"
              size="small"
              icon={<Dismiss20Regular />}
              aria-label="Dismiss shortage notice"
              onClick={shortageNotice.onDismiss}
            />
          </MessageBarActions>
        </MessageBar>
      )}
      {!readOnly && !chargesOnly && (
        <ProductSearchInput
          size="large"
          disabled={pricingResolving}
          onInventoryItemSelect={onProductSelect}
          fromDate={pickupDate ? new Date(pickupDate + 'T00:00:00').toISOString() : undefined}
          tillDate={returnDate ? new Date(returnDate + 'T23:59:59').toISOString() : undefined}
        />
      )}
      {lines.length === 0 ? (
        chargesOnly ? null : (
        <div className={styles.emptyState}>
          <Text weight="semibold" style={{ fontSize: '16px' }}>
            {isPhase
              ? 'This phase is empty. Get started by adding some products or a custom line.'
              : 'This order is empty. Get started by adding some products or a custom line.'}
          </Text>
        </div>
        )
      ) : (
        <div className={styles.linesTable}>
          <div className={styles.lineHeader}>
            {/* Headings left-align at the column start. Item and Total are
                self-evident, so their header labels are intentionally blank. */}
            <span aria-hidden />
            <Text size={200} weight="bold">Qty</Text>
            <Text size={200} weight="bold">Charge</Text>
            <Text size={200} weight="bold">Rental cycle</Text>
            <span aria-hidden />
            <span aria-hidden />
          </div>
          {lines.map((line) => {
            const priceEmpty = !line.priceInCents;
            // Optimistic placeholder: the add-line POST is still in flight, so
            // there's no real line id yet — render a non-interactive row with a
            // spinner instead of the editable qty/price/remove controls.
            if (line.pending) {
              return (
                <div key={line.id} className={styles.lineRow} style={{ opacity: 0.6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Spinner size="tiny" />
                    <Text>{line.name}</Text>
                  </div>
                  <Text className={styles.lineQty}>{line.quantity}</Text>
                  <Text className={styles.linePrice}>Adding…</Text>
                  <Text className={styles.lineRentalCycle}>—</Text>
                  <Text className={styles.lineTotalPending}>—</Text>
                  <span aria-hidden />
                </div>
              );
            }
            // Type-derived display affordances for this line — one source of
            // truth in place of the per-affordance conditionals that used to
            // live inline here. See resolveLineDisplaySpec / LINE_RULESHEET.
            const spec = resolveLineDisplaySpec(line);
            // Per-line note (Booqable line.extra_information). Hidden behind a
            // caret; defaults open when the line already has a saved note.
            const canAddNote = !!onUpdateLineExtraInfo && spec.extraInfo && !readOnly;
            const noteOpen = extraInfoOpen[line.id] ?? !!line.extraInformation;
            // When locked, still surface any saved note as static text.
            const showReadOnlyNote = readOnly && spec.extraInfo && !!line.extraInformation;
            // Only weighed loads get an inline tonnage + landfill-receipt
            // panel: roll-off and grapple-truck pickups physically go to a
            // landfill/transfer station and get scale-weighed. Front loaders
            // match the tonnage-applicable name heuristic but are billed flat
            // and quantity-tracked — no tonnage is captured for them — so they
            // are explicitly excluded here. Scoped to the line; needs the saved
            // order (local uuid) to attach to.
            const showTonnage =
              !!orderLocalId &&
              isTonnageApplicableProductName(line.name) &&
              !isFrontLoaderLine(line) &&
              !readOnly;
            const tonnagePanelOpen = tonnageOpen[line.id] ?? false;
            // A started/stopped planning is locked in Booqable, which 422s on
            // removing the line — so we hide the X. We don't surface a per-line
            // fulfillment status: orders start/complete as a whole, not
            // line-by-line, so a per-line "Started"/"Returned" label is just
            // Booqable noise here.
            const locked = !!(line.started || line.stopped);
            return (
              <div key={line.id} className={styles.lineRowWrap}>
                <div className={styles.lineRow}>
                <div>
                  <div className={styles.lineNameRow}>
                    {draggableLines && (
                      <span
                        className={styles.dragHandle}
                        draggable
                        role="button"
                        aria-label={`Drag ${line.name} to a phase`}
                        title="Drag to a phase"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/x-line-id', line.id);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                      >
                        <ReOrderDotsVertical20Regular />
                      </span>
                    )}
                    {canAddNote && (
                      <Button
                        appearance="transparent"
                        size="small"
                        className={styles.lineCaret}
                        icon={noteOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                        aria-label={noteOpen ? `Hide note for ${line.name}` : `Add note for ${line.name}`}
                        aria-expanded={noteOpen}
                        title="Extra information"
                        onClick={() =>
                          setExtraInfoOpen((prev) => ({ ...prev, [line.id]: !noteOpen }))
                        }
                      />
                    )}
                    <Text weight="bold">{lineDisplayLabels.get(line.id) ?? line.name}</Text>
                  </div>
                  {showTonnage && (
                    <Button
                      appearance="subtle"
                      size="small"
                      className={styles.tonnageToggle}
                      icon={<Receipt20Regular />}
                      aria-expanded={tonnagePanelOpen}
                      onClick={() =>
                        setTonnageOpen((prev) => ({ ...prev, [line.id]: !tonnagePanelOpen }))
                      }
                    >
                      Tonnage &amp; receipt
                    </Button>
                  )}
                </div>
                <div className={styles.lineQty}>
                  {spec.showQty && <LineQty line={line} onUpdate={onUpdateLineQuantity} readOnly={readOnly} />}
                </div>
                <div className={styles.linePrice}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <LinePriceInput line={line} onUpdate={onUpdateLinePrice} noLocationHint={serviceAddressMissing} readOnly={readOnly} />
                  </div>
                  {line.pricingSource &&
                   line.pricingSource !== 'tier_table' &&
                   // 'unresolved' is the pre-service-address / still-loading
                   // state — already signaled by the orange "Capture service
                   // address to see tier" pill at the top of the section. Don't
                   // surface a raw "unresolved" chip on the line as well.
                   line.pricingSource !== 'unresolved' && (
                    <span style={{
                      display: 'inline-block',
                      marginTop: '2px',
                      padding: '1px 5px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      fontWeight: 600,
                      // Orange (Pumpkin) accent = warning, reserved for the
                      // genuine fallback (a roll-off that couldn't be tier-
                      // priced): white fill + orange border + orange text.
                      // Plain 'default price' (flat-fee products like the bag)
                      // is the intended state, so it gets a neutral chip.
                      backgroundColor: line.pricingSource === 'product_default_fallback'
                        ? tokens.colorNeutralBackground1
                        : tokens.colorNeutralBackground3,
                      border: line.pricingSource === 'product_default_fallback'
                        ? `1px solid ${tokens.colorPalettePumpkinBorderActive}`
                        : 'none',
                      color: line.pricingSource === 'product_default_fallback'
                        ? tokens.colorPalettePumpkinBorderActive
                        : tokens.colorNeutralForeground3,
                      whiteSpace: 'nowrap',
                    }}>
                      {line.pricingSource === 'product_default_fallback' ? '⚠ fallback price' :
                       line.pricingSource === 'product_default' ? 'default price' :
                       line.pricingSource}
                    </span>
                  )}
                </div>
                {/* Rental cycle — N/A for sales items (bulldog bag), whose
                    price is a flat sale price, not a per-week rate. A grapple
                    pickup is a live-load service (the truck waits while it's
                    loaded), so it shows "Live Load" rather than a day count. */}
                <div className={styles.lineRentalCycle}>
                  {isGrapplePickupLine(line) ? (
                    <Text style={{ whiteSpace: 'nowrap' }}>Live Load</Text>
                  ) : isMonthly && spec.rentalDuration ? (
                    <Text style={{ whiteSpace: 'nowrap' }}>Monthly</Text>
                  ) : days !== null && spec.rentalDuration ? (
                    <Text style={{ whiteSpace: 'nowrap' }}>
                      {days} {days === 1 ? 'day' : 'days'}
                    </Text>
                  ) : (
                    <Text className={styles.lineTotalPending}>—</Text>
                  )}
                </div>
                {priceEmpty ? (
                  <Text className={styles.lineTotalPending}>—</Text>
                ) : (
                  <Text className={styles.lineTotal}>{fmtUsd(line.totalInCents)}</Text>
                )}
                {/* Action cell: optional Move-to-phase menu (phased orders) +
                    the remove X. A started/stopped planning line can't be
                    removed (Booqable 422s on destroying its planning), so the X
                    hides; the grid cell stays so column alignment is preserved. */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
                  {onMoveLineToPhase && phaseOptions && !readOnly && (
                    <MovePhaseMenu
                      phases={phaseOptions}
                      currentSectionId={lineSectionId ? lineSectionId(line.id) : null}
                      onMove={(sid) => onMoveLineToPhase(line.id, sid)}
                    />
                  )}
                  {spec.removable && !locked && !readOnly ? (
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Dismiss20Regular />}
                      aria-label={`Remove ${line.name}`}
                      title="Remove line"
                      onClick={() => onRemoveLine?.(line.id)}
                    />
                  ) : (
                    <span aria-hidden />
                  )}
                </div>
                </div>
                {/* Edge-to-edge rule + stock pills below the title row. Pulled
                    out of the name cell so the divider spans the full line
                    width rather than just the item column. Trackable rentals
                    only — sales items have no individual units to specify. */}
                {spec.stockUnits && (
                  <>
                    <Divider className={styles.lineTitleDivider} />
                    <LineStockPills
                      line={line}
                      orderId={orderIdForStockPicker}
                      onChange={onStockItemsChanged}
                    />
                  </>
                )}
                {/* Full-width note, revealed by the caret. */}
                {canAddNote && noteOpen && (
                  <LineExtraInfo line={line} onUpdate={onUpdateLineExtraInfo} />
                )}
                {showReadOnlyNote && <LineExtraInfo line={line} readOnly />}
                {/* Inline tonnage + landfill-receipt capture for this container.
                    Kept mounted and height-animated (grid-rows 0fr↔1fr) so
                    toggling expands/collapses smoothly instead of snapping the
                    rows below up and down. */}
                {showTonnage && (
                  <div
                    className={mergeClasses(
                      styles.tonnageDisclosure,
                      tonnagePanelOpen && styles.tonnageDisclosureOpen,
                    )}
                  >
                    <div className={styles.tonnageDisclosureInner}>
                      <LineTonnageCard
                        inline
                        orderId={orderLocalId!}
                        line={{ id: line.id, label: lineDisplayLabels.get(line.id) ?? line.name }}
                        savedNet={netByLine.get(line.id) ?? null}
                        onLinesChanged={onTonnageLinesChanged}
                        customerName={customerName}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Section — hidden in compact (phase-card) mode: per-phase
          custom-line/discount/coupon and totals live at the order level. */}
      {!compact && (
      <div className={styles.summarySection}>
        {!readOnly && (
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="outline" size="medium" iconPosition="after" icon={<ChevronDown20Regular />}>
                Add line
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<AppsList24Regular />} onClick={() => setCustomLineOpen(true)}>
                  Custom line
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
        <CustomLineDialog
          isOpen={customLineOpen}
          onClose={() => setCustomLineOpen(false)}
          onSubmit={async (payload) => {
            if (onAddCustomLine) await onAddCustomLine(payload);
          }}
        />
        <DiscountDialog
          isOpen={discountDialogOpen}
          onClose={() => setDiscountDialogOpen(false)}
          subtotalInCents={subtotalInCents ?? 0}
        />
        <CouponDialog
          isOpen={couponDialogOpen}
          onClose={() => setCouponDialogOpen(false)}
        />
        {/* Phased orders hide the totals block (deferred to the sidebar), so the
            in-summary "Adjust PR withholding" link below never renders for them.
            Surface the same control here, in the order-charges block, since the
            holdback is an order-level figure. Non-phased orders (hideTotals=false)
            use the in-summary link instead, so this never double-renders. */}
        {hideTotals && !readOnly && onSetWithholdingAmount && (
          <FluentLink
            className={styles.summaryLink}
            onClick={() => setWithholdingDialogOpen(true)}
          >
            {withholdingAmountCents != null
              ? 'Edit PR withholding adjustment'
              : 'Adjust PR withholding'}
          </FluentLink>
        )}
        {onSetWithholdingAmount && (
          <WithholdingAdjustmentDialog
            isOpen={withholdingDialogOpen}
            onClose={() => setWithholdingDialogOpen(false)}
            totalInCents={totalInCents ?? subtotalInCents ?? 0}
            currentAmountCents={withholdingAmountCents ?? null}
            autoAmountCents={autoWithholdingCents ?? 0}
            autoRateLabel={autoWithholdingRate}
            onSave={onSetWithholdingAmount}
          />
        )}

        {!hideTotals && (
        <div className={styles.summaryRight}>
          {(() => {
            const convenienceFeeCents = lines
              .filter((l) => l.kind === 'convenience_fee')
              .reduce((sum, l) => sum + (l.totalInCents || 0), 0);
            const displaySubtotal = (subtotalInCents ?? 0) - convenienceFeeCents;
            return (
              <>
                <div className={styles.summaryRow}>
                  <Text className={styles.summaryLabel}>Subtotal</Text>
                  <Text className={styles.summaryValue}>{fmtUsd(displaySubtotal)}</Text>
                </div>

                {!readOnly && (
                  <div className={styles.summaryRow}>
                    <FluentLink
                      className={styles.summaryLink}
                      onClick={() => setDiscountDialogOpen(true)}
                    >
                      Add a discount
                    </FluentLink>
                  </div>
                )}

                {!readOnly && (
                  <div className={styles.summaryRow}>
                    <FluentLink
                      className={styles.summaryLink}
                      onClick={() => setCouponDialogOpen(true)}
                    >
                      Add a coupon
                    </FluentLink>
                  </div>
                )}

                <div className={styles.summaryRow}>
                  <Text className={styles.summaryLabel}>Total discount</Text>
                  <Text className={styles.summaryValue}>$0.00</Text>
                </div>

                {/* PR services-withholding — held back by the customer and
                    remitted to Hacienda. Computed on the pre-tax subtotal, so it
                    sits with the pre-tax totals (above the tax line); recognized
                    revenue (Total) is unchanged, only the Balance due drops. */}
                {prWithholdingInCents !== undefined && prWithholdingInCents > 0 && (
                  <div className={styles.summaryRow}>
                    <Text className={styles.summaryLabel}>
                      PR withholding{prWithholdingRate ? ` (${prWithholdingRate})` : ''}
                    </Text>
                    <Text className={styles.summaryValue}>−{fmtUsd(prWithholdingInCents)}</Text>
                  </div>
                )}

                {/* Manual withholding adjustment — a one-off flat holdback that
                    overrides the automatic rate (e.g. correcting the 10% a prior
                    invoice didn't deduct). */}
                {!readOnly && onSetWithholdingAmount && (
                  <div className={styles.summaryRow}>
                    <FluentLink
                      className={styles.summaryLink}
                      onClick={() => setWithholdingDialogOpen(true)}
                    >
                      {withholdingAmountCents != null
                        ? 'Edit PR withholding adjustment'
                        : 'Adjust PR withholding'}
                    </FluentLink>
                  </div>
                )}

                <div className={styles.summaryRow}>
                  <Text className={styles.summaryLabel}>
                    {taxLabel ? `Tax (${taxLabel})` : 'Tax'}
                  </Text>
                  <Text className={styles.summaryValue}>{fmtUsd(taxInCents ?? 0)}</Text>
                </div>

                {convenienceFeeCents > 0 && (
                  <div className={styles.summaryRow}>
                    <Text className={styles.summaryLabel}>Convenience fee</Text>
                    <Text className={styles.summaryValue}>{fmtUsd(convenienceFeeCents)}</Text>
                  </div>
                )}
              </>
            );
          })()}

          <div className={styles.summaryDivider} />

          <div className={styles.totalRow}>
            <Text>Total incl. taxes</Text>
            <Text>{fmtUsd(totalInCents ?? subtotalInCents ?? 0)}</Text>
          </div>

          {/* Net collectible after the PR withholding deducted above. Recognized
              revenue (Total) is unchanged; only the Balance due drops. */}
          {prWithholdingInCents !== undefined && prWithholdingInCents > 0 && (
            <div className={styles.totalRow}>
              <Text>Balance due</Text>
              <Text>
                {fmtUsd(
                  Math.max(0, (totalInCents ?? subtotalInCents ?? 0) - prWithholdingInCents),
                )}
              </Text>
            </div>
          )}

        </div>
        )}
      </div>
      )}

      <BaseDialog
        isOpen={tierMapOpen}
        onClose={() => setTierMapOpen(false)}
        title="Rate Tier Map"
        maxWidth="1100px"
        showCloseButton
      >
        <React.Suspense fallback={<div className={styles.tierMapFallback}><Spinner /></div>}>
          <RateTierMap compact highlightMunicipio={municipalityName ?? undefined} />
        </React.Suspense>
      </BaseDialog>
    </SectionShell>
  );
};

export const ProductsSection = React.memo(ProductsSectionBase);
export default ProductsSection;
